import { useCallback, useState } from "react";
import { getAuthToken } from "components/common/authTokenManager";
import { ProfilePictureUploadResponse, UseProfilePictureUploadResult } from "../types";

const { REACT_APP_V2_API_ENDPOINT } = process.env;

export const useProfilePictureUpload = (): UseProfilePictureUploadResult => {
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setProgress(0);
    setUploading(false);
    setError(null);
  }, []);

  const upload = useCallback((file: File): Promise<ProfilePictureUploadResponse> => {
    setUploading(true);
    setProgress(0);
    setError(null);

    const formData = new FormData();
    formData.append("image", file);

    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${REACT_APP_V2_API_ENDPOINT}/profile-picture`);
      xhr.withCredentials = true;
      const token = getAuthToken();
      if (token) xhr.setRequestHeader("X-Token", token);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        setUploading(false);
        try {
          const res: ProfilePictureUploadResponse = JSON.parse(xhr.responseText);
          if (!res.isSuccessful) {
            setError(res.error || "Upload failed. Please try again.");
          }
          resolve(res);
        } catch {
          const message = "Upload failed. Please try again.";
          setError(message);
          resolve({ isSuccessful: false, error: message });
        }
      };

      xhr.onerror = () => {
        setUploading(false);
        const message = "Upload failed. Please check your connection and try again.";
        setError(message);
        resolve({ isSuccessful: false, error: message });
      };

      xhr.send(formData);
    });
  }, []);

  return { upload, progress, uploading, error, reset };
};
